import React, { useState, useEffect } from 'react'
import {
  Flex,
  Menu,
  Button,
  MenuList,
  MenuItem,
  Text,
  MenuButton,
  Box,
  Input
} from '@chakra-ui/react'
import { MdKeyboardArrowDown } from '@icons/index'
import { useResponsive } from '@common/hooks'
import { useQuery } from '@tanstack/react-query'
import FilterTag from './filter-tag'
import { listBrands } from '@lib/util/brand-handler'
import { listCategories, listCollections } from '@lib/util/category-handler'
import RouterUrl from '@constants/router.constant'
import { useRouter } from 'next/router'
import useFilterQueryParams from '@common/hooks/use-filter-query-param'
import { SortFilter } from '@constants/filter.constant'
import { ProductCollection } from '@medusajs/medusa'

export interface MapDataFilter {
  title: string
  handle: string
  value: string
}

interface MenuFilterPropsType {
  data: MapDataFilter[]
  enableSearch?: boolean
  onChoose: (handle?: string) => void
  text: string
}

const MenuFilter = ({
  data,
  enableSearch,
  onChoose,
  text
}: MenuFilterPropsType) => {
  const [search, setSearch] = useState<string | null>(null)
  const [mapData, setMapData] = useState<MapDataFilter[]>([])
  const refInputSearch = React.useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!search || (search && search.trim().length === 0)) {
      setMapData(data.sort((a: any, b: any) => a.title.localeCompare(b.title)))
    } else {
      const searchMap = data
        .filter((element) =>
          element.title.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a: any, b: any) => a.title.localeCompare(b.title))

      setMapData(searchMap)

      // To focus on input search after render again
      setTimeout(() => {
        refInputSearch.current?.focus()
      }, 100)
    }
  }, [search, data])

  return (
    <Menu>
      <MenuButton
        as={Button}
        rightIcon={<MdKeyboardArrowDown />}
        bg={'white !important'}
        justifyContent={'end'}
        p={0}
        gap={2}
        _hover={{
          bg: 'white'
        }}
        _focus={{
          bg: 'white'
        }}
      >
        <Text color={'black'} textStyle="filterText">
          {text}
        </Text>
      </MenuButton>
      <MenuList h={200} overflowY={'scroll'}>
        {enableSearch && (
          <Box px={1}>
            <Input
              type={'text'}
              px={2}
              mb={2}
              ref={refInputSearch}
              placeholder={'Search'}
              onChange={(e: any) => {
                setSearch(e.target.value)
              }}
            />
          </Box>
        )}
        {mapData.map((item) => (
          <MenuItem
            key={item.value}
            onClick={() => {
              onChoose(item.handle)
            }}
          >
            {item.title}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  )
}

const ShopFilter = ({ hides }: { hides: string[] }) => {
  const { mdMax } = useResponsive()
  const router = useRouter()

  const {
    addCategory,
    addBrand,
    addSort,
    addCollection,
    addAll,
    clearAllQuery
  } = useFilterQueryParams()

  const [filterCategories, setFilterCategories] = useState<MapDataFilter[]>([])
  const [filterCollections, setFilterCollections] = useState<MapDataFilter[]>(
    []
  )
  const [filterBrands, setFilterBrands] = useState<MapDataFilter[]>([])
  const [filterSort, setFilterSort] = useState<MapDataFilter[]>(
    SortFilter as MapDataFilter[]
  )

  const { data } = useQuery(['categories'], () => listCategories())
  const { data: brands } = useQuery(['brands'], () => listBrands())
  const { data: collections } = useQuery(['collections'], () =>
    listCollections({ limit: 999999 })
  )

  useEffect(() => {
    if (data && data.product_categories) {
      const cats = data.product_categories.map((pc: any) => ({
        title: pc.name,
        handle: pc.handle,
        value: pc.id
      })) as MapDataFilter[]
      setFilterCategories(cats)
    }
  }, [data])

  useEffect(() => {
    if (brands && brands.data) {
      const brads = brands.data.map((b: any) => ({
        title: b.title,
        handle: b.handle,
        value: b.id
      })) as MapDataFilter[]
      setFilterBrands(brads)
    }
  }, [brands])

  useEffect(() => {
    if (collections && collections.collections) {
      const customCollections = collections.collections
        .map((collection: ProductCollection) => ({
          title: collection.title,
          handle: collection.handle,
          value: collection.id,
          metadata: collection.metadata
        }))
        .filter((collection) => !collection.metadata.isBrand) as MapDataFilter[]

      setFilterCollections(customCollections)
    }
  }, [collections])

  return (
    <Box mt={10} mb={10}>
      <Flex
        justifyContent={'space-between'}
        flexDirection={mdMax ? 'column' : undefined}
        gap={10}
        flexWrap="wrap"
      >
        <Flex gap={10} alignItems="center" wrap="wrap">
          <Flex>
            <MenuFilter
              data={[{ title: 'All', handle: 'all', value: 'all' }]}
              onChoose={() => clearAllQuery()}
              text={'ALL'}
            />
          </Flex>
          {!hides.includes('brand') && (
            <Flex>
              <MenuFilter
                text={'BRAND'}
                data={filterBrands}
                enableSearch={true}
                onChoose={(handle?: string) => {
                  if (handle) {
                    addBrand(handle)
                  }
                }}
              />
            </Flex>
          )}
          {!hides.includes('collection') && (
            <Flex>
              <MenuFilter
                text={'COLLECTIONS'}
                data={filterCollections}
                enableSearch={true}
                onChoose={(handle?: string) => {
                  if (handle) {
                    addCollection(handle)
                  }
                }}
              />
            </Flex>
          )}
          {!hides.includes('category') && (
            <Flex>
              <MenuFilter
                text={'CATEGORIES'}
                data={filterCategories}
                enableSearch={true}
                onChoose={(handle?: string) => {
                  if (handle) {
                    addCategory(handle)
                  }
                }}
              />
            </Flex>
          )}
          <Flex
            cursor={'pointer'}
            onClick={() => router.push(RouterUrl.DEALS.ROOT)}
          >
            <Text color={'red'} textStyle="filterText">
              SALE
            </Text>
          </Flex>
        </Flex>
        <Flex gap={7} alignItems={'center'}>
          <Text
            textStyle="filterText"
            cursor={'pointer'}
            onClick={() => addAll()}
          >
            VIEW ALL
          </Text>
          <MenuFilter
            text={'SORT'}
            data={filterSort}
            onChoose={(handle?: string) => {
              if (handle) {
                addSort(handle)
              }
            }}
          />
        </Flex>
      </Flex>
      <FilterTag
        filterBrands={filterBrands}
        filterCategories={filterCategories}
        filterCollections={filterCollections}
        filterSort={filterSort}
      />
    </Box>
  )
}

export default ShopFilter
